// The recommendation words, tones and the playbook's score keys as the debrief and decision views read them (lane B14,
// R4/R5, DC-245/DC-249). Words and tones come only from the design system's mapping (C5: `recommendationWord`), never
// from a guess over the stored value; the average core score, its band and the offered set come only from the server
// (summary rows and snapshot, C3; `debrief.open.level`, C4). Every new key is optional on read: a snapshot frozen
// before B14 carries none of them and is drawn with "—", never back-filled.

import { recommendationWord, recoPillTone, type Tone } from "@/mc/labels";
import type { DebriefOpenOutput } from "@/api/tools";

export interface RecoWord {
  value: string;
  label: string;
  tone: Tone;
  earlierScale: boolean;
}

/** The stored recommendation's word and pill hue; an unknown value reads as nothing (never a guess). */
export function recoWord(value: unknown): RecoWord | null {
  if (typeof value !== "string" || !value) return null;
  const word = recommendationWord(value);
  return word ? { value, label: word.label, tone: recoPillTone(word.tone), earlierScale: word.earlierScale } : null;
}

/** The rubric band the server read for an average (C9): {key, text, highlight}. */
export interface RubricBand {
  key: string;
  text: string;
  highlight: boolean;
}

export function bandOf(raw: unknown): RubricBand | null {
  if (!raw || typeof raw !== "object") return null;
  const band = raw as Record<string, unknown>;
  if (typeof band.key !== "string" || typeof band.text !== "string") return null;
  return { key: band.key, text: band.text, highlight: band.highlight === true };
}

/** An average core score as the server sends it ("4.50"), or null. */
export const averageOf = (raw: unknown): string | null => (typeof raw === "string" && raw.trim() ? raw : null);

/** A count the server sends, or null when the key is absent (a frozen snapshot). */
export const countOf = (raw: unknown): number | null => (typeof raw === "number" && Number.isFinite(raw) ? raw : null);

/** The playbook's display order (C0 PLAYBOOK_RECOMMENDATIONS): the tally reads it, then any earlier-scale value. */
export const PLAYBOOK_ORDER = ["strong_hire", "hire", "consider_other_role", "no_hire"] as const;

/** Hire · No Hire, the level-v1 set: the room's fallback when a read carries no level block. */
export const LEVEL_V1_OFFERED: readonly string[] = ["hire", "no_hire"];

/** The room's level block (C4): the band, the offered set in display order, the values that count as Hire. */
export interface RoomLevel {
  band: string | null;
  offered: string[];
  hireLike: string[];
}

const strings = (raw: unknown): string[] => (Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : []);

/** `debrief.open.level` (C4); without one, Hire · No Hire and Hire alone counting as Hire (the level-v1 rule). */
export function levelOf(view: DebriefOpenOutput | null | undefined): RoomLevel {
  const raw = (view as { level?: unknown } | null | undefined)?.level;
  const level = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
  const offered = strings(level?.offered).filter((v) => recommendationWord(v) !== null);
  const hireLike = strings(level?.hire_like);
  return {
    band: typeof level?.band === "string" ? level.band : null,
    offered: offered.length ? offered : [...LEVEL_V1_OFFERED],
    hireLike: hireLike.length ? hireLike : ["hire"],
  };
}

/** The tally's entries in playbook order, then any other value a card was recorded with (an earlier-scale card). */
export function tallyOf(raw: unknown): { value: string; count: number }[] | null {
  if (!raw || typeof raw !== "object") return null;
  const tally = raw as Record<string, unknown>;
  const first = PLAYBOOK_ORDER.map((value) => ({ value, count: countOf(tally[value]) ?? 0 }));
  const rest = Object.entries(tally)
    .filter(([value, n]) => !(PLAYBOOK_ORDER as readonly string[]).includes(value) && (countOf(n) ?? 0) > 0 && recommendationWord(value) !== null)
    .map(([value, n]) => ({ value, count: countOf(n) ?? 0 }));
  return [...first, ...rest];
}
